import React, { ReactNode, useMemo } from 'react';
import { cn } from '@chapche/ui';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export type SortableDragHandleProps = {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
    setActivatorNodeRef: (element: HTMLElement | null) => void;
};

type SortableItemModel = {
    id: string;
};

type SortableListProps<T extends SortableItemModel> = {
    items: T[];
    onReorder: (orderedIds: string[]) => void;
    renderItem: (item: T, handleProps: SortableDragHandleProps, index: number) => ReactNode;
    disabled?: boolean;
    className?: string;
};

type SortableRowProps<T extends SortableItemModel> = {
    item: T;
    index: number;
    disabled?: boolean;
    renderItem: SortableListProps<T>['renderItem'];
};

const SortableRow = <T extends SortableItemModel>({
    item,
    index,
    disabled,
    renderItem,
}: SortableRowProps<T>) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: item.id,
        disabled,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : undefined,
        zIndex: isDragging ? 2 : undefined,
        position: 'relative',
    };

    return (
        <div ref={setNodeRef} style={style} className={cn(isDragging && 'sortable-list__row_dragging cursor-grabbing')}>
            {renderItem(item, { attributes, listeners, setActivatorNodeRef }, index)}
        </div>
    );
};

/**
 * Reusable vertical sortable list built on @dnd-kit.
 * Consumer renders row content and attaches drag handle via handleProps.
 */
export const SortableList = <T extends SortableItemModel>({
    items,
    onReorder,
    renderItem,
    disabled = false,
    className = '',
}: SortableListProps<T>) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const ids = useMemo(() => items.map((item) => item.id), [items]);

    const handleDragEnd = (event: DragEndEvent) => {
        if (disabled) {
            return;
        }
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = ids.indexOf(String(active.id));
        const newIndex = ids.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }
        const nextIds = arrayMove(ids, oldIndex, newIndex);
        onReorder(nextIds);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={ids} strategy={verticalListSortingStrategy} disabled={disabled}>
                <div className={cn('sortable-list flex flex-col', className)} role="list">
                    {items.map((item, index) => (
                        <SortableRow
                            key={item.id}
                            item={item}
                            index={index}
                            disabled={disabled}
                            renderItem={renderItem}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

export default SortableList;
